import { Card, Checkbox, Col, Form, Input, message, Radio, Row, Space, Switch, Tooltip, Typography } from "antd";
import { Formik } from "formik";
import React, { Dispatch, SetStateAction } from "react";
import * as Yup from "yup";
import { CSUserProfile } from "../../../models/csuser-profile-interface";
import { SketchPicker } from 'react-color';
import { Profile } from "../../../models/profile-interface";
import { UserRole } from "../../../models/user-role-interface";
import _ from "lodash";
import { CustomerServiceQueries } from "../../../queries/customer-service-queries";
import { useHistory } from "react-router";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

type Props = {
  formRef: React.MutableRefObject<any>;
  selectedUser: CSUserProfile;
  profile: Profile;
  roles: UserRole[];
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  allUsers: CSUserProfile[];
}

const UserForm: React.FC<Props> = ({
  formRef,
  selectedUser,
  profile,
  roles,
  setIsSubmitting,
  allUsers
}) => {
  const userPutMutation = CustomerServiceQueries.usePutUser(selectedUser);
  const userPostMutation = CustomerServiceQueries.usePostUser(selectedUser);
  const history = useHistory();

  const handleSubmit = (values : CSUserProfile) => {
    var userProfile: CSUserProfile = _.clone(values);
    if(selectedUser?.userId > 0) {
      userProfile.userId = selectedUser.userId;
      userProfile.id = selectedUser.id;

      userPutMutation.mutateAsync(userProfile).then((results) => {
        if(!(results.status >= 400)) {
          history.push('/admin/users/manage');
          message.success("Successfully updated user");
        }
      }).finally(() => {
        setIsSubmitting(false);
      });
    } else {
      userPostMutation.mutateAsync(userProfile).then(() => {
        history.push('/admin/users/manage');
        message.success("Successfully created user");
      }).finally(() => {
        setIsSubmitting(false);
      });
    }
  }

  return <Formik
    innerRef={formRef}
    initialValues={selectedUser != null ? {
      name: selectedUser.name,
      alias: selectedUser.alias,
      email: selectedUser.email,
      roleId: selectedUser.roleId,
      slackId: selectedUser.slackId,
      isSalesRep: selectedUser.isSalesRep,
      hexColor: selectedUser.hexColor,
      isActive: selectedUser.isActive
    } : {
      name: '',
      alias: null,
      email: null,
      roleId: null,
      slackId: null,
      isSalesRep: false,
      hexColor: 'FFFFFF',
      isActive: true
    }}
    validationSchema={Yup.object().shape({
      name: Yup.string().required('Please enter a name for this user').typeError('Please enter a name for this user'),
      alias: Yup.string().nullable(),
      email: Yup.string().required('Please enter an email').typeError('Please enter an email').test(
        'emailValid', 'This email is not valid', (val) => {
          let regex = /^(?!.*@govenda\.com$)^(?!.*@boardbookit\.com$)^(?!.*@onboardmeetings\.com$)/s;
          return !regex.test(val);
        }).test('duplicate-email-check', 'User is already added to this company', val => {
          const sameEmail = (allUsers ?? [])
              .find(u => String(u.email)?.toLowerCase() === String(val)?.toLowerCase());
          if (!!sameEmail)
              if (selectedUser?.userId !== sameEmail?.userId)
                  return false;
          return true;
      }),
      roleId: Yup.number().required('Please select a Role for this user').typeError('Please select a Role for this user'),
      slackId: Yup.string().nullable(),
      isSalesRep: Yup.boolean(),
      hexColor: Yup.string(),
      isActive: Yup.boolean(),
    })}
    onSubmit={(values) => {
      setIsSubmitting(true);
      handleSubmit(values);
    }}
    enableReinitialize
  >
      {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          dirty
      }) => {
        return (<Card title={selectedUser != null ? 'Edit User' : 'Create User'} 
        style={{width: '100%'}}
        extra={<Switch
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          disabled={selectedUser?.userId == profile?.userId}
          checked={values?.isActive}
          onClick={() => setFieldValue('isActive', !values?.isActive)}
        />}>
          <Form layout="vertical">
            <Row gutter={[16, 16]}>
              
              <Col sm={24} lg={12}>
                <Col span={24}>
                  <Form.Item
                    label={<>Email &nbsp; <Tooltip title="Must be a valid @govenda.com, @boardbookit.com, or @onboardmeetings.com email address"><InfoCircleOutlined /></Tooltip></>}
                    help={(touched.email && errors.email ? errors.email : null)}
                    validateStatus={touched.email && errors.email ? 'error' : ''}
                    required
                  >
                    <Input
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="Name"
                    help={(touched.name && errors.name ? errors.name : null)}
                    validateStatus={touched.name && errors.name ? 'error' : ''}
                    required
                  >
                    <Input
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={<>Slack Member ID &nbsp; <Tooltip title="Used for tagging the user in slack for things such as signup notifications. To get a users member ID in slack, click their name in the top of a conversation > View Full Profile > Click the 3 dot menu > select Copy Member ID"><InfoCircleOutlined /></Tooltip></>}
                    help={(touched.slackId && errors.slackId ? errors.slackId : null)}
                    validateStatus={touched.slackId && errors.slackId ? 'error' : ''}
                  >
                    <Input
                      name="slackId"
                      value={values.slackId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Item>
                </Col>
              </Col>
              <Col sm={24} lg={12}>
                <Row gutter={[16,16]}>
                <Col span={24}>
                  <Card title="User Type">
                    <Form.Item
                      className="full-width-label"
                      style={{ marginBottom: 0 }}
                      help={(touched.roleId && errors.roleId ? errors.roleId : null)}
                      validateStatus={touched.roleId && errors.roleId ? 'error' : ''}
                    >
                      <Radio.Group
                        onChange={handleChange}
                        value={values.roleId}
                        name="roleId"
                        disabled={profile?.userId === selectedUser?.userId}
                      >
                        <Space direction="vertical" style={{ rowGap: 0, columnGap: 0 }}>
                          {roles?.map((r, i) => (
                            <Radio value={r.id} key={`role${i}`}>
                              <Text strong>{r.name}</Text>
                              {!!r.description && <p style={{ fontSize: 11 }}>{r.description}</p>}
                            </Radio>
                          ))}
                        </Space>
                      </Radio.Group>
                    </Form.Item>
                  </Card>
                </Col>
                <Col span={24}>
                  <Card>
                    <Checkbox
                      name="isSalesRep"
                      checked={values.isSalesRep}
                      onChange={handleChange}
                    >
                      <b>Sales Representative</b>
                    </Checkbox>
                    <div>Indicates that his user is a sales representative and show show up on the drop down as such to be associated with a company signup</div>
                  </Card>
                </Col>
                </Row>
              </Col>

            </Row>
          </Form></Card>
        )
      }}
  </Formik>
}

export default UserForm;